import { ChangeEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import M from 'materialize-css';
import queryHooks from '../../common/queryHooks/index.ts';
import moment from 'moment';
import { OutboundDocumentAPI } from '../../common/api/outboundDocument.ts';
import { HiOutlineDocumentArrowDown } from 'react-icons/hi2';
import { BsThreeDots } from 'react-icons/bs';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../common/api/index.ts';
import { OutboundDocumentStatus } from '../../common/types/models.ts';
import {
  OutboundDocumentStatusEnum,
  OutboundDocumentStatusMethodEnum,
} from '../../common/enums.ts';
import { useQueryClient } from '@tanstack/react-query';
import { OUTBOUND_DOCUMENT_STATUS_QUERY_KEY } from '../../common/queryHooks/outboundDocumentStatus.ts';
import { useDebounceValue } from '../../common/useDebounce.ts';
import useCurrentCustomerLocationId from '../../hooks/useCurrentCustomerLocation.ts';

const SentEpcisDocumentsOverview = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { customerLocationId } = useCurrentCustomerLocationId();

  const tradingPartnerIdSelect = useRef<HTMLSelectElement>(null);

  const initValues = () => ({
    customerLocationId:
      searchParams.get('customerLocationId') || Number(customerLocationId),
    tradingPartnerId: searchParams.get('tradingPartnerId') || '',
    dateSentStart: searchParams.get('dateSentStart') || '',
    dateSentEnd: searchParams.get('dateSentEnd') || '',
    ndc: searchParams.get('ndc') || '',
    medName: searchParams.get('medName') || '',
    serialNumber: searchParams.get('serialNumber') || '',
  });

  const [values, setValues] = useState(initValues());
  const [showAdvancedSearch, setShowAdvancedSearch] = useState<boolean>(() => {
    const { dateSentEnd, dateSentStart, ndc, medName, serialNumber } = values;
    return Boolean(
      dateSentEnd || dateSentStart || ndc || medName || serialNumber
    );
  });
  const [displayList, setDisplayList] = useState<
    OutboundDocumentAPI.Response.ReadManySearch[]
  >([]);

  const debouncedValues = useDebounceValue(values, 500);

  const onChange = ({
    target: { id, value },
  }: ChangeEvent<HTMLSelectElement | HTMLInputElement>) =>
    setValues(prev => ({ ...prev, [id]: value }));

  const { data: tradingPartnerList } =
    queryHooks.tradingPartner.useReadAllByUserId();

  const {
    data: outboundDocumentList,
    refetch,
    isLoading,
    isSuccess,
  } = queryHooks.outboundDocument.useReadManySearch(debouncedValues, {
    enabled: true,
    refetchInterval: 10000,
  });

  useEffect(() => {
    M.updateTextFields();
    const urlSearchParams = new URLSearchParams();
    Object.entries(values).forEach(([key, value]) => {
      if (value) urlSearchParams.set(key, value.toString());
    });

    setSearchParams(urlSearchParams, { replace: true });

    if (
      Object.values(values).some(val => Boolean(val)) &&
      !outboundDocumentList
    )
      refetch();
  }, [values, outboundDocumentList, refetch, setSearchParams]);

  useEffect(() => {
    if (tradingPartnerIdSelect.current)
      M.FormSelect.init(tradingPartnerIdSelect.current);
  }, [tradingPartnerList]);

  useEffect(() => {
    if (Array.isArray(outboundDocumentList))
      setDisplayList(
        outboundDocumentList.filter(
          ({ id }, index, self) =>
            self.findIndex(({ id: innerId }) => innerId === id) === index
        )
      );
  }, [outboundDocumentList]);

  useEffect(() => {
    if (isSuccess)
      queryClient.invalidateQueries({
        queryKey: [OUTBOUND_DOCUMENT_STATUS_QUERY_KEY],
      });
  }, [isSuccess, queryClient]);

  useEffect(() => {
    const id = Number(customerLocationId);

    setValues(prev =>
      prev.customerLocationId === id
        ? prev
        : { ...prev, customerLocationId: id }
    );
  }, [customerLocationId]);

  const search = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    refetch();
  };

  const downloadDocument = async (outboundDocumentId: number | string) => {
    const {
      outboundDocument: {
        customerLocation: { name },
        tradingPartner: { name: tpName },
        documentIdentifier,
      },
      buffer,
    } = await api.outboundDocument.download.one.by.id(outboundDocumentId);
    const uint8Array = Uint8Array.from(buffer.data);
    const blob = new Blob([uint8Array]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.hidden = true;
    link.href = url;
    link.setAttribute(
      'download',
      `${name}_to_${tpName}_${documentIdentifier.slice(-8)}.xml`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getLatestStatus = (statuses?: OutboundDocumentStatus[]) => {
    const last = statuses?.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )[0];

    const status = last ? last.status.toLocaleLowerCase() : 'default';
    const method =
      last?.method === OutboundDocumentStatusMethodEnum.as2
        ? 'Via AS2'
        : 'Via Email';

    switch (status) {
      case OutboundDocumentStatusEnum.sent:
        return <span className="green-text bold">Sent {method}</span>;
      case OutboundDocumentStatusEnum.failed:
      case OutboundDocumentStatusEnum.incomplete:
        return <span className="red-text bold">Failed {method}</span>;
      case OutboundDocumentStatusEnum.queued:
        return <span className="purple-text bold">Pending</span>;
      default:
        return <span className="green-text bold">Sent {method}</span>;
    }
  };

  useEffect(() => {
    if (!showAdvancedSearch)
      setValues(prev => ({
        ...prev,
        medName: '',
        serialNumber: '',
        ndc: '',
        dateSentEnd: '',
        dateSentStart: '',
      }));
  }, [showAdvancedSearch]);

  return (
    <div>
      <div className="row">
        <div className="row">
          <a
            href="/"
            className="right"
            onClick={e => {
              e.preventDefault();
              setShowAdvancedSearch(prev => !prev);
            }}
          >
            {showAdvancedSearch ? 'Hide' : 'Show'} Advanced Search
          </a>
        </div>
        <div className="input-field col s12 m12">
          <select
            ref={tradingPartnerIdSelect}
            id="tradingPartnerId"
            value={values.tradingPartnerId}
            onChange={onChange}
          >
            <option value="">All Trading Partners</option>
            {tradingPartnerList?.map(({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          <label htmlFor="tradingPartnerId">Trading Partner</label>
        </div>
      </div>
      {showAdvancedSearch && (
        <>
          <div className="row">
            <div className="input-field col s12 m6">
              <input
                id="medName"
                type="text"
                value={values.medName}
                onChange={onChange}
              />
              <label htmlFor="medName">Med Name</label>
            </div>
            <div className="input-field col s12 m3">
              <input
                id="ndc"
                type="text"
                value={values.ndc}
                onChange={onChange}
              />
              <label htmlFor="ndc">NDC</label>
            </div>
            <div className="input-field col s12 m3">
              <input
                id="serialNumber"
                type="text"
                value={values.serialNumber}
                onChange={onChange}
              />
              <label htmlFor="serialNumber">Serial #</label>
            </div>
          </div>
          <div className="row">
            <div className="input-field col s12 m3">
              <input
                id="dateSentStart"
                type="date"
                value={values.dateSentStart}
                onChange={onChange}
              />
              <label htmlFor="dateSentStart">Date Sent Start</label>
            </div>
            <div className="input-field col s12 m3">
              <input
                id="dateSentEnd"
                type="date"
                value={values.dateSentEnd}
                onChange={onChange}
              />
              <label htmlFor="dateSentEnd">Date Sent End</label>
            </div>
          </div>
        </>
      )}
      <div className="row">
        <div className="input-field col s12 m4 offset-m8">
          <a
            href="/"
            className="btn-small blue white-text waves-effect waves-light col s12"
            onClick={search}
          >
            Search
          </a>
        </div>
      </div>
      <div className="row">
        {isLoading ? (
          <div className="progress">
            <div className="indeterminate blue" />
          </div>
        ) : displayList?.length ? (
          <table className="highlight">
            <thead>
              <tr>
                <th>Status</th>
                <th>Location</th>
                <th>Trading Partner</th>
                <th>PO #</th>
                <th>Date Sent</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayList.map(outboundDocument => {
                const {
                  id,
                  customerLocation,
                  tradingPartner,
                  timestamp,
                  outboundDocumentStatuses,
                  poIdentifier,
                } = outboundDocument;

                return (
                  <tr key={id}>
                    <td style={{ padding: '4px' }}>
                      {getLatestStatus(outboundDocumentStatuses)}
                    </td>
                    <td style={{ padding: '4px' }}>{customerLocation.name}</td>
                    <td style={{ padding: '4px' }}>{tradingPartner.name}</td>
                    <td style={{ padding: '4px' }}>{poIdentifier ?? '-'}</td>
                    <td style={{ padding: '4px' }}>
                      {moment(timestamp).format('MM/DD/YY')}
                    </td>
                    <td
                      style={{
                        padding: '4px',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-evenly',
                      }}
                    >
                      <HiOutlineDocumentArrowDown
                        style={{
                          fontSize: '1.3em',
                          position: 'relative',
                          top: '3px',
                          cursor: 'pointer',
                        }}
                        onClick={() => downloadDocument(id)}
                      />
                      <Link to={`/sentepcisdocs/${id}`}>
                        <BsThreeDots
                          style={{
                            fontSize: '1.3em',
                            position: 'relative',
                            top: '3px',
                            cursor: 'pointer',
                            color: 'black',
                          }}
                        />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <span className="orange-text">No Results</span>
        )}
      </div>
    </div>
  );
};

export default SentEpcisDocumentsOverview;
